import './App.css';
import TitleBar from './components/TitleBar';
import logo from './images/OLI-logo-white.svg';
import atomIcon from './images/atom_icon_transparent.svg';
import gasWellIcon from './images/gas_new.svg';
import pressureIcon from './images/pressure-icon.png';
import { useState, useEffect, useRef } from 'react';
import BulkDataModal from './components/BulkDataModal';

function App() {
  const [showOverlay, setShowOverlay] = useState(false);
  const [wellName, setWellName] = useState('');
  const [showError, setShowError] = useState(false);
  const [showConditions, setShowConditions] = useState(false);
  const [showSampleData, setShowSampleData] = useState(false);
  const [tempUnit, setTempUnit] = useState('°C');
  const [pressureUnit, setPressureUnit] = useState('bar');
  const [showTempMenu, setShowTempMenu] = useState(false);
  const [showPressureMenu, setShowPressureMenu] = useState(false);
  const [showConcentrationMenu, setShowConcentrationMenu] = useState({
    cations: false,
    anions: false,
    neutrals: false
  });
  const [showMoleMenu, setShowMoleMenu] = useState({
    gasComponents: false,
    gasAssays: false,
    oilComponents: false,
    oilPseudoComponents: false,
    oilAssays: false
  });
  const [activeReservoirTab, setActiveReservoirTab] = useState('water');
  const [activeWaterTab, setActiveWaterTab] = useState('chemistry');
  const [waterData, setWaterData] = useState({
    cations: [
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' }
    ],
    anions: [
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' }
    ],
    neutrals: [
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' }
    ]
  });
  const [gasData, setGasData] = useState({
    components: [
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' }
    ],
    assays: [
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' }
    ]
  });
  const [oilData, setOilData] = useState({
    components: [
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' }
    ],
    pseudoComponents: [
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' }
    ],
    assays: [
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' },
      { component: '', amount: '' }
    ]
  });
  const [conditions, setConditions] = useState({
    separator: { temp: '', pressure: '' },
    chokeValve: { temp: '', pressure: '' },
    wellHead: { temp: '', pressure: '' },
    downhole: { temp: '', pressure: '' },
    reservoir: { temp: '', pressure: '' }
  });
  const [focusedRow, setFocusedRow] = useState(null);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteModalData, setPasteModalData] = useState('');
  const [activePasteTable, setActivePasteTable] = useState(null);
  const [pastePreview, setPastePreview] = useState({
    cations: [],
    anions: [],
    neutrals: []
  });
  const [pasteError, setPasteError] = useState('');
  const [errorLine, setErrorLine] = useState(null);
  const [concentrationUnit, setConcentrationUnit] = useState({
    cations: 'mg/L',
    anions: 'mg/L',
    neutrals: 'mg/L'
  });
  const [moleUnit, setMoleUnit] = useState({
    gasComponents: 'mol %',
    gasAssays: 'mol %',
    oilComponents: 'mol %',
    oilPseudoComponents: 'mol %',
    oilAssays: 'mol %'
  });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [waterProperties, setWaterProperties] = useState({
    pH: '',
    alkalinity: '',
    tds: '',
    density: '',
    conductivity: '',
    tic: ''
  });
  
  const [alkalinityUnit, setAlkalinityUnit] = useState('mg/L as CaCO₃');
  const [showAlkalinityMenu, setShowAlkalinityMenu] = useState(false);
  const [densityUnit, setDensityUnit] = useState('g/ml');
  const [showDensityMenu, setShowDensityMenu] = useState(false);
  const [conductivityUnit, setConductivityUnit] = useState('µmho/cm');
  const [showConductivityMenu, setShowConductivityMenu] = useState(false);
  const [ticUnit, setTicUnit] = useState('mol C/L');
  const [showTicMenu, setShowTicMenu] = useState(false);
  const [showBulkDataModal, setShowBulkDataModal] = useState(false);

  const tempMenuRef = useRef(null);
  const pressureMenuRef = useRef(null);
  const concentrationMenuRef = useRef(null);

  // Toggle to show/hide the gas assays table
  const showGasAssaysTable = false;

  const handleTempClick = (e) => {
    e.stopPropagation();
    setShowTempMenu(!showTempMenu);
    setShowPressureMenu(false);
    setShowConcentrationMenu({
      cations: false,
      anions: false,
      neutrals: false
    });
  };

  const handlePressureClick = (e) => {
    e.stopPropagation();
    setShowPressureMenu(!showPressureMenu);
    setShowTempMenu(false);
    setShowConcentrationMenu({
      cations: false,
      anions: false,
      neutrals: false
    });
  };

  const handleConcentrationClick = (tableName) => {
    setShowConcentrationMenu(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
    // Close mole menu if open
    setShowMoleMenu(prev => ({
      ...prev,
      [tableName]: false
    }));
  };

  const handleMoleClick = (tableName) => {
    setShowMoleMenu(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
    // Close concentration menu if open
    setShowConcentrationMenu(prev => ({
      ...prev,
      [tableName]: false
    }));
  };

  const handleAlkalinityUnitClick = (e) => {
    e.stopPropagation();
    setShowAlkalinityMenu(!showAlkalinityMenu);
  };

  const handleAlkalinityUnitSelect = (unit) => {
    setAlkalinityUnit(unit);
    setShowAlkalinityMenu(false);
  };

  const handleDensityUnitClick = (e) => {
    e.stopPropagation();
    setShowDensityMenu(!showDensityMenu);
  };

  const handleDensityUnitSelect = (unit) => {
    setDensityUnit(unit);
    setShowDensityMenu(false);
  };

  const handleConductivityUnitClick = (e) => {
    e.stopPropagation();
    setShowConductivityMenu(!showConductivityMenu);
  };

  const handleConductivityUnitSelect = (unit) => {
    setConductivityUnit(unit);
    setShowConductivityMenu(false);
  };

  const handleTicUnitClick = (e) => {
    e.stopPropagation();
    setShowTicMenu(!showTicMenu);
  };

  const handleTicUnitSelect = (unit) => {
    setTicUnit(unit);
    setShowTicMenu(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const tempSelector = document.querySelector('.temp-selector');
      const pressureSelector = document.querySelector('.pressure-selector');
      const concentrationSelectors = document.querySelectorAll('.concentration-selector');
      const moleSelectors = document.querySelectorAll('.mole-selector');
      const alkalinitySelector = document.querySelector('.alkalinity-selector');
      const densitySelector = document.querySelector('.density-selector');
      const conductivitySelector = document.querySelector('.conductivity-selector');
      const ticSelector = document.querySelector('.tic-selector');
      
      if (tempSelector && !tempSelector.contains(event.target)) {
        setShowTempMenu(false);
      }
      if (pressureSelector && !pressureSelector.contains(event.target)) {
        setShowPressureMenu(false);
      }
      if (alkalinitySelector && !alkalinitySelector.contains(event.target)) {
        setShowAlkalinityMenu(false);
      }
      if (densitySelector && !densitySelector.contains(event.target)) {
        setShowDensityMenu(false);
      }
      if (conductivitySelector && !conductivitySelector.contains(event.target)) {
        setShowConductivityMenu(false);
      }
      if (ticSelector && !ticSelector.contains(event.target)) {
        setShowTicMenu(false);
      }
      concentrationSelectors.forEach(selector => {
        if (selector && !selector.contains(event.target)) {
          const tableName = selector.getAttribute('data-table');
          if (tableName) {
            setShowConcentrationMenu(prev => ({
              ...prev,
              [tableName]: false
            }));
          }
        }
      });
      moleSelectors.forEach(selector => {
        if (selector && !selector.contains(event.target)) {
          const tableName = selector.getAttribute('data-table');
          if (tableName) {
            setShowMoleMenu(prev => ({
              ...prev,
              [tableName]: false
            }));
          }
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFileSelect = (event) => {
    const files = event.target.files;
    // Handle the selected files here
    console.log('Selected files:', files);
  };

  const handleInputChange = (location, type, value) => {
    // Only allow numbers, decimals, minus sign, and E for scientific notation
    const numberRegex = /^-?\d*\.?\d*[Ee]?-?\d*$/;
    if (value === '' || numberRegex.test(value)) {
      setConditions(prev => ({
        ...prev,
        [location]: {
          ...prev[location],
          [type]: value
        }
      }));
    }
  };

  const handleInputBlur = (location, type, value) => {
    if (value !== '') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        setConditions(prev => ({
          ...prev,
          [location]: {
            ...prev[location],
            [type]: num.toFixed(2)
          }
        }));
      }
    }
  };

  const hasValidConditions = () => {
    const locations = Object.values(conditions);
    return locations.some(loc => {
      const temp = parseFloat(loc.temp);
      const pressure = parseFloat(loc.pressure);
      return !isNaN(temp) && !isNaN(pressure);
    });
  };

  const handleNext = () => {
    if (!showConditions) {
      if (!wellName.trim()) {
        setShowError(true);
      } else {
        setShowError(false);
        setShowConditions(true);
      }
    } else {
      if (hasValidConditions()) {
        setShowConditions(false);
        setShowSampleData(true);
      }
    }
  };

  const handleCancel = () => {
    setShowOverlay(false);
    setShowSampleData(false);
    setWellName('');
    setShowError(false);
    setShowConditions(false);
    setConditions({
      separator: { temp: '', pressure: '' },
      chokeValve: { temp: '', pressure: '' },
      wellHead: { temp: '', pressure: '' },
      downhole: { temp: '', pressure: '' },
      reservoir: { temp: '', pressure: '' }
    });
    setTempUnit('°C');
    setPressureUnit('bar');
  };

  const handleUnitSelect = (unit, type, tableName) => {
    if (type === 'temp') {
      setTempUnit(unit);
      setShowTempMenu(false);
    } else if (type === 'pressure') {
      setPressureUnit(unit);
      setShowPressureMenu(false);
    } else if (type === 'concentration') {
      setConcentrationUnit(prev => ({
        ...prev,
        [tableName]: unit
      }));
      setShowConcentrationMenu(prev => ({
        ...prev,
        [tableName]: false
      }));
    } else if (type === 'mole') {
      setMoleUnit(prev => ({
        ...prev,
        [tableName]: unit
      }));
      setShowMoleMenu(prev => ({
        ...prev,
        [tableName]: false
      }));
    }
  };

  const handleBack = () => {
    setShowSampleData(false);
    setShowConditions(true);
  };

  const handleWaterDataChange = (index, field, value) => {
    if (field === 'amount') {
      // Only allow numbers, decimals, minus sign, and E for scientific notation
      const numberRegex = /^-?\d*\.?\d*[Ee]?-?\d*$/;
      if (value === '' || numberRegex.test(value)) {
        setWaterData(prev => {
          const newData = { ...prev };
          const table = Object.keys(newData).find(key => 
            newData[key].some((_, i) => i === index)
          );
          if (table) {
            newData[table] = [...newData[table]];
            newData[table][index] = { ...newData[table][index], [field]: value };
          }
          return newData;
        });
      }
    } else {
      setWaterData(prev => {
        const newData = { ...prev };
        const table = Object.keys(newData).find(key => 
          newData[key].some((_, i) => i === index)
        );
        if (table) {
          newData[table] = [...newData[table]];
          newData[table][index] = { ...newData[table][index], [field]: value };
        }
        return newData;
      });
    }
  };

  const handleGasDataChange = (index, field, value) => {
    if (field === 'amount') {
      // Only allow numbers, decimals, minus sign, and E for scientific notation
      const numberRegex = /^-?\d*\.?\d*[Ee]?-?\d*$/;
      if (value === '' || numberRegex.test(value)) {
        setGasData(prev => {
          const newData = { ...prev };
          const table = Object.keys(newData).find(key => 
            newData[key].some((_, i) => i === index)
          );
          if (table) {
            newData[table] = [...newData[table]];
            newData[table][index] = { ...newData[table][index], [field]: value };
          }
          return newData;
        });
      }
    } else {
      setGasData(prev => {
        const newData = { ...prev };
        const table = Object.keys(newData).find(key => 
          newData[key].some((_, i) => i === index)
        );
        if (table) {
          newData[table] = [...newData[table]];
          newData[table][index] = { ...newData[table][index], [field]: value };
        }
        return newData;
      });
    }
  };

  const handleOilDataChange = (index, field, value) => {
    if (field === 'amount') {
      // Only allow numbers, decimals, minus sign, and E for scientific notation
      const numberRegex = /^-?\d*\.?\d*[Ee]?-?\d*$/;
      if (value === '' || numberRegex.test(value)) {
        setOilData(prev => {
          const newData = { ...prev };
          const table = Object.keys(newData).find(key => 
            newData[key].some((_, i) => i === index)
          );
          if (table) {
            newData[table] = [...newData[table]];
            newData[table][index] = { ...newData[table][index], [field]: value };
          }
          return newData;
        });
      }
    } else {
      setOilData(prev => {
        const newData = { ...prev };
        const table = Object.keys(newData).find(key => 
          newData[key].some((_, i) => i === index)
        );
        if (table) {
          newData[table] = [...newData[table]];
          newData[table][index] = { ...newData[table][index], [field]: value };
        }
        return newData;
      });
    }
  };

  const handleAddRow = (dataType, tableName) => {
    if (dataType === 'water') {
      setWaterData(prev => ({
        ...prev,
        [tableName]: [...prev[tableName], { component: '', amount: '' }]
      }));
    } else if (dataType === 'gas') {
      setGasData(prev => ({
        ...prev,
        [tableName]: [...prev[tableName], { component: '', amount: '' }]
      }));
    } else if (dataType === 'oil') {
      setOilData(prev => ({
        ...prev,
        [tableName]: [...prev[tableName], { component: '', amount: '' }]
      }));
    }
  };

  const handleDeleteRow = (dataType, tableName, index) => {
    if (dataType === 'water') {
      setWaterData(prev => ({
        ...prev,
        [tableName]: prev[tableName].filter((_, i) => i !== index)
      }));
    } else if (dataType === 'gas') {
      setGasData(prev => ({
        ...prev,
        [tableName]: prev[tableName].filter((_, i) => i !== index)
      }));
    } else if (dataType === 'oil') {
      setOilData(prev => ({
        ...prev,
        [tableName]: prev[tableName].filter((_, i) => i !== index)
      }));
    }
    setFocusedRow(null);
  };

  const handlePasteClick = (dataType, tableName) => {
    setActivePasteTable({ dataType, tableName });
    setShowPasteModal(true);
    setPasteModalData('');
    setPasteError('');
    setPastePreview({
      cations: [],
      anions: [],
      neutrals: []
    });
  };

  // Validation for cation paste
  const validateCationPasteData = (data) => {
    const lines = data.split('\n').filter(line => line.trim());
    const preview = [];
    let hasError = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const parts = line.split(/\s+/).filter(Boolean);
      if (parts.length < 2) {
        setPasteError(`Line ${i + 1}: Missing amount value`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      const combinedParts = [];
      for (let j = 0; j < parts.length - 1; j++) {
        if (parts[j] === '(*)' && combinedParts.length > 0) {
          combinedParts[combinedParts.length - 1] += '(*)';
        } else {
          combinedParts.push(parts[j]);
        }
      }
      const componentPart = combinedParts.join('');
      const cleanedCationComponent = componentPart.replace(/\s+/g, '');
      const amount = parts[parts.length - 1];
      if (!cleanedCationComponent) {
        setPasteError(`Line ${i + 1}: Missing component name`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      if (isNaN(amount)) {
        setPasteError(`Line ${i + 1}: Amount must be a number`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      if (!cleanedCationComponent.match(/(\+|\(\+\d*\))(\(\*\))?$/)) {
        setPasteError(`Line ${i + 1}: Not a valid cation format`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      preview.push({ component: cleanedCationComponent, amount });
    }
    if (!hasError) {
      setPasteError('');
      setErrorLine(null);
      setPastePreview(preview);
    }
    return !hasError;
  };

  // Validation for anion paste
  const validateAnionPasteData = (data) => {
    const lines = data.split('\n').filter(line => line.trim());
    const preview = [];
    let hasError = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const parts = line.split(/\s+/).filter(Boolean);
      if (parts.length < 2) {
        setPasteError(`Line ${i + 1}: Missing amount value`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      const combinedParts = [];
      for (let j = 0; j < parts.length - 1; j++) {
        if (parts[j] === '(*)' && combinedParts.length > 0) {
          combinedParts[combinedParts.length - 1] += '(*)';
        } else {
          combinedParts.push(parts[j]);
        }
      }
      const componentPart = combinedParts.join('');
      const cleanedAnionComponent = componentPart.replace(/\s+/g, '');
      const amount = parts[parts.length - 1];
      if (!cleanedAnionComponent) {
        setPasteError(`Line ${i + 1}: Missing component name`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      if (isNaN(amount)) {
        setPasteError(`Line ${i + 1}: Amount must be a number`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      if (!cleanedAnionComponent.match(/(-|-\d+|\(-\d+\))(\(\*\))?$/)) {
        setPasteError(`Line ${i + 1}: Not a valid anion format`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      preview.push({ component: cleanedAnionComponent, amount });
    }
    if (!hasError) {
      setPasteError('');
      setErrorLine(null);
      setPastePreview(preview);
    }
    return !hasError;
  };

  // Validation for neutral paste
  const validateNeutralPasteData = (data) => {
    const lines = data.split('\n').filter(line => line.trim());
    const preview = [];
    let hasError = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const parts = line.split(/\s+/).filter(Boolean);
      if (parts.length < 2) {
        setPasteError(`Line ${i + 1}: Missing amount value`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      const combinedParts = [];
      for (let j = 0; j < parts.length - 1; j++) {
        if (parts[j] === '(*)' && combinedParts.length > 0) {
          combinedParts[combinedParts.length - 1] += '(*)';
        } else {
          combinedParts.push(parts[j]);
        }
      }
      const componentPart = combinedParts.join('');
      const cleanedNeutralComponent = componentPart.replace(/\s+/g, '');
      const amount = parts[parts.length - 1];
      if (!cleanedNeutralComponent) {
        setPasteError(`Line ${i + 1}: Missing component name`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      if (isNaN(amount)) {
        setPasteError(`Line ${i + 1}: Amount must be a number`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      if (cleanedNeutralComponent.match(/(\+|\(\+\d*\))(\(\*\))?$/) || cleanedNeutralComponent.match(/(-|-\d+|\(-\d+\))(\(\*\))?$/)) {
        setPasteError(`Line ${i + 1}: Not a valid neutral format`);
        setErrorLine(i);
        hasError = true;
        break;
      }
      preview.push({ component: cleanedNeutralComponent, amount });
    }
    if (!hasError) {
      setPasteError('');
      setErrorLine(null);
      setPastePreview(preview);
    }
    return !hasError;
  };

  // Update handlePasteDataChange to use the correct validator
  const handlePasteDataChange = (e) => {
    const newData = e.target.value;
    setPasteModalData(newData);
    if (activePasteTable?.tableName === 'cations') {
      validateCationPasteData(newData);
    } else if (activePasteTable?.tableName === 'anions') {
      validateAnionPasteData(newData);
    } else if (activePasteTable?.tableName === 'neutrals') {
      validateNeutralPasteData(newData);
    }
  };

  // Update handlePasteSubmit to use the correct validator
  const handlePasteSubmit = () => {
    if (activePasteTable?.tableName === 'cations') {
      if (validateCationPasteData(pasteModalData)) {
        setShowPreviewModal(true);
      }
    } else if (activePasteTable?.tableName === 'anions') {
      if (validateAnionPasteData(pasteModalData)) {
        setShowPreviewModal(true);
      }
    } else if (activePasteTable?.tableName === 'neutrals') {
      if (validateNeutralPasteData(pasteModalData)) {
        setShowPreviewModal(true);
      }
    }
  };

  const handlePreviewConfirm = () => {
    if (activePasteTable) {
      const { dataType, tableName } = activePasteTable;
      const newData = pastePreview;

      if (dataType === 'water') {
        if (Array.isArray(newData)) {
          // Individual paste: update only the relevant table
          setWaterData(prev => ({
            ...prev,
            [tableName]: newData
          }));
        } else {
          // Bulk paste: update all tables
          setWaterData(prev => ({
            ...prev,
            cations: newData.cations,
            anions: newData.anions,
            neutrals: newData.neutrals
          }));
        }
      } else if (dataType === 'gas') {
        setGasData(prev => ({
          ...prev,
          [tableName]: newData
        }));
      } else if (dataType === 'oil') {
        setOilData(prev => ({
          ...prev,
          [tableName]: newData
        }));
      }
    }
    setShowPasteModal(false);
    setShowPreviewModal(false);
    setPasteModalData('');
    setPasteError('');
    setPastePreview({
      cations: [],
      anions: [],
      neutrals: []
    });
    setErrorLine(null);
    setActivePasteTable(null);
  };

  const handlePreviewCancel = () => {
    setShowPreviewModal(false);
  };

  const handlePasteCancel = () => {
    setShowPasteModal(false);
    setPasteModalData('');
    setPasteError('');
    setPastePreview({
      cations: [],
      anions: [],
      neutrals: []
    });
    setErrorLine(null);
    setActivePasteTable(null);
  };

  const renderPreviewModal = () => {
    if (!showPreviewModal) return null;

    // Determine if pastePreview is an array (individual paste) or object (bulk)
    const isArrayPreview = Array.isArray(pastePreview);

    return (
      <div className="paste-modal-overlay">
        <div className="paste-modal">
          <h3>Preview Data</h3>
          <div className="paste-preview">
            {activePasteTable?.dataType === 'water' && !isArrayPreview ? (
              <>
                <h4>Cations</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastePreview.cations.map((row, index) => (
                      <tr key={index}>
                        <td>{row.component}</td>
                        <td>{row.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <h4>Anions</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastePreview.anions.map((row, index) => (
                      <tr key={index}>
                        <td>{row.component}</td>
                        <td>{row.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <h4>Neutrals</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pastePreview.neutrals.map((row, index) => (
                      <tr key={index}>
                        <td>{row.component}</td>
                        <td>{row.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {isArrayPreview && pastePreview.map((row, index) => (
                    <tr key={index}>
                      <td>{row.component}</td>
                      <td>{row.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="paste-modal-buttons">
            <button className="cancel-button" onClick={handlePreviewCancel}>Cancel</button>
            <button 
              className="next-button" 
              onClick={handlePreviewConfirm}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPasteModal = () => {
    if (!showPasteModal) return null;

    const isArrayPreview = Array.isArray(pastePreview);
    const hasData = isArrayPreview
      ? pastePreview.length > 0
      : (pastePreview.cations.length > 0 || pastePreview.anions.length > 0 || pastePreview.neutrals.length > 0);

    return (
      <div className="paste-modal-overlay">
        <div className="paste-modal">
          <h3>Paste Data</h3>
          <div className="paste-instructions">
            <p>Enter data in the format: component amount (one per line)</p>
            {activePasteTable?.dataType === 'water' && !isArrayPreview && (
              <p className="paste-instructions-note">
                Components will be automatically sorted into cations (ending with +), anions (ending with -), and neutrals.
              </p>
            )}
          </div>
          <div className="textarea-container">
            <textarea
              value={pasteModalData}
              onChange={handlePasteDataChange}
              placeholder="Na+ 1000&#10;Ca2+ 500&#10;Cl- 200&#10;CO2 100"
              rows={10}
              className={pasteError ? 'error' : ''}
            />
            {errorLine !== null && (
              <div 
                className="error-highlight"
                style={{
                  top: `${errorLine * 2}rem`,
                  height: '1.5em'
                }}
              />
            )}
          </div>
          {pasteError && (
            <div className="paste-error">{pasteError}</div>
          )}
          <div className="paste-modal-buttons">
            <button className="cancel-button" onClick={handlePasteCancel}>Cancel</button>
            <button 
              className="next-button" 
              onClick={handlePasteSubmit}
              disabled={!!pasteError || !hasData}
            >
              Add data
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTable = (title, data, onChange, dataType, tableName) => (
    <div className="reservoir-table-container">
      <table 
        className={`reservoir-table${dataType === 'gas' && tableName === 'assays' ? ' gas-assays-table' : ''}`}
      >
        <thead>
          <tr>
            <th>
              <h4>{title}</h4>
            </th>
            <th>
              {dataType === 'water' && (
                <div className="unit-selector concentration-selector" data-table={tableName}>
                  <span 
                    className="header-group" 
                    onClick={() => handleConcentrationClick(tableName)}
                    style={{ padding: '4px' }}
                  >
                    {concentrationUnit[tableName]}
                  </span>
                  {showConcentrationMenu[tableName] && (
                    <div className="unit-menu">
                      <div style={{ textAlign: 'left', cursor: 'pointer' }} onMouseOver={(e) => e.target.style.textDecoration = 'underline'} onMouseOut={(e) => e.target.style.textDecoration = 'none'} onClick={() => handleUnitSelect('mg/L', 'concentration', tableName)}>mg/L</div>
                      <div style={{ textAlign: 'left', cursor: 'pointer' }} onMouseOver={(e) => e.target.style.textDecoration = 'underline'} onMouseOut={(e) => e.target.style.textDecoration = 'none'} onClick={() => handleUnitSelect('g/L', 'concentration', tableName)}>g/L</div>
                      <div style={{ textAlign: 'left', cursor: 'pointer' }} onMouseOver={(e) => e.target.style.textDecoration = 'underline'} onMouseOut={(e) => e.target.style.textDecoration = 'none'} onClick={() => handleUnitSelect('kg/m³', 'concentration', tableName)}>kg/m³</div>
                      <div style={{ textAlign: 'left', cursor: 'pointer' }} onMouseOver={(e) => e.target.style.textDecoration = 'underline'} onMouseOut={(e) => e.target.style.textDecoration = 'none'} onClick={() => handleUnitSelect('lb/ft³', 'concentration', tableName)}>lb/ft³</div>
                      <div style={{ textAlign: 'left', cursor: 'pointer' }} onMouseOver={(e) => e.target.style.textDecoration = 'underline'} onMouseOut={(e) => e.target.style.textDecoration = 'none'} onClick={() => handleUnitSelect('lb/gal', 'concentration', tableName)}>lb/gal</div>
                      <div style={{ textAlign: 'left', cursor: 'pointer' }} onMouseOver={(e) => e.target.style.textDecoration = 'underline'} onMouseOut={(e) => e.target.style.textDecoration = 'none'} onClick={() => handleUnitSelect('mass %', 'concentration', tableName)}>mass %</div>
                      <div style={{ textAlign: 'left', cursor: 'pointer' }} onMouseOver={(e) => e.target.style.textDecoration = 'underline'} onMouseOut={(e) => e.target.style.textDecoration = 'none'} onClick={() => handleUnitSelect('ppm (mass)', 'concentration', tableName)}>ppm (mass)</div>
                    </div>
                  )}
                </div>
              )}
              {(dataType === 'gas' || dataType === 'oil') && (
                <div className="unit-selector mole-selector" data-table={tableName}>
                  <span 
                    className="header-group" 
                    onClick={() => handleMoleClick(tableName)}
                    style={{ padding: '4px' }}
                  >
                    {moleUnit[tableName] || 'mol %'}
                  </span>
                  {showMoleMenu[tableName] && (
                    <div className="unit-menu">
                      <div style={{ textAlign: 'left', cursor: 'pointer' }} onMouseOver={(e) => e.target.style.textDecoration = 'underline'} onMouseOut={(e) => e.target.style.textDecoration = 'none'} onClick={() => handleUnitSelect('mol %', 'mole', tableName)}>mol %</div>
                      <div style={{ textAlign: 'left', cursor: 'pointer' }} onMouseOver={(e) => e.target.style.textDecoration = 'underline'} onMouseOut={(e) => e.target.style.textDecoration = 'none'} onClick={() => handleUnitSelect('ppm (mole)', 'mole', tableName)}>ppm (mole)</div>
                      <div style={{ textAlign: 'left', cursor: 'pointer' }} onMouseOver={(e) => e.target.style.textDecoration = 'underline'} onMouseOut={(e) => e.target.style.textDecoration = 'none'} onClick={() => handleUnitSelect('mol/mol', 'mole', tableName)}>mol/mol</div>
                    </div>
                  )}
                </div>
              )}
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr 
              key={index}
              onFocus={() => setFocusedRow(`${dataType}-${tableName}-${index}`)}
              onBlur={(e) => {
                if (!e.relatedTarget?.classList.contains('delete-row-button')) {
                  setFocusedRow(null);
                }
              }}
              tabIndex={0}
            >
              <td>
                <input
                  type="text"
                  value={row.component}
                  onChange={(e) => onChange(index, 'component', e.target.value)}
                />
              </td>
              <td>
                <input
                  type="text"
                  className="number-entry"
                  value={row.amount}
                  onChange={(e) => onChange(index, 'amount', e.target.value)}
                />
              </td>
              <td className="delete-cell">
                {focusedRow === `${dataType}-${tableName}-${index}` && (
                  <button 
                    className="delete-row-button"
                    onClick={() => handleDeleteRow(dataType, tableName, index)}
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="table-actions">
        <button 
          className="add-row-button"
          onClick={() => handleAddRow(dataType, tableName)}
          data-tooltip="Add row"
        >
          <span>+</span>
        </button>
        <button 
          className="paste-button"
          onClick={() => handlePasteClick(dataType, tableName)}
          data-tooltip="Paste data"
        >
          <i className="fa fa-paste"></i>
        </button>
      </div>
      {showPasteModal && activePasteTable?.dataType === dataType && activePasteTable?.tableName === tableName && (
        renderPasteModal()
      )}
    </div>
  );

  const handleWaterPropertyChange = (property, value) => {
    // Only allow numbers, decimals, minus sign, and E for scientific notation
    const numberRegex = /^-?\d*\.?\d*[Ee]?-?\d*$/;
    if (value === '' || numberRegex.test(value)) {
      setWaterProperties(prev => ({
        ...prev,
        [property]: value
      }));
    }
  };

  const handleWaterPropertyBlur = (property, value) => {
    if (value !== '') {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        setWaterProperties(prev => ({
          ...prev,
          [property]: num.toFixed(2)
        }));
      }
    }
  };

  const renderWaterPropertiesTable = () => (
    <div className="reservoir-table-container">
      <table className="reservoir-table water-properties-table">
        <thead>
          <tr>
            <th>
              <h4>Recorded Properties</h4>
            </th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Measured pH</td>
            <td className="input-size-3rem">
              <input
                type="text"
                className="number-entry input-size-3rem"
                value={waterProperties.pH}
                onChange={(e) => handleWaterPropertyChange('pH', e.target.value)}
                onBlur={(e) => handleWaterPropertyBlur('pH', e.target.value)}
              />
            </td>
            <td>pH</td>
          </tr>
          <tr>
            <td>Measured Alkalinity</td>
            <td className="input-size-3rem">
              <input
                type="text"
                className="number-entry input-size-3rem"
                value={waterProperties.alkalinity}
                onChange={(e) => handleWaterPropertyChange('alkalinity', e.target.value)}
                onBlur={(e) => handleWaterPropertyBlur('alkalinity', e.target.value)}
              />
            </td>
            <td>
              <div className="unit-selector alkalinity-selector">
                <span 
                  className="header-group" 
                  onClick={handleAlkalinityUnitClick}
                  style={{ padding: '4px' }}
                >
                  {alkalinityUnit}
                </span>
                {showAlkalinityMenu && (
                  <div className="unit-menu">
                    <div onClick={() => handleAlkalinityUnitSelect('eq/L')}>eq/L</div>
                    <div onClick={() => handleAlkalinityUnitSelect('meq/L')}>meq/L</div>
                    <div onClick={() => handleAlkalinityUnitSelect('mg/L HCO₃')}>mg/L HCO₃</div>
                    <div onClick={() => handleAlkalinityUnitSelect('mol/L HCO₃')}>mol/L HCO₃</div>
                    <div onClick={() => handleAlkalinityUnitSelect('mg/L CO₃')}>mg/L CO₃</div>
                    <div onClick={() => handleAlkalinityUnitSelect('mol/L CO₃')}>mol/L CO₃</div>
                    <div onClick={() => handleAlkalinityUnitSelect('mg/L as CaCO₃')}>mg/L as CaCO₃</div>
                    <div onClick={() => handleAlkalinityUnitSelect('mol/L as CaCO₃')}>mol/L as CaCO₃</div>
                  </div>
                )}
              </div>
            </td>
          </tr>
          <tr>
            <td>Total Dissolved Solids</td>
            <td className="input-size-3rem">
              <input
                type="text"
                className="number-entry input-size-3rem"
                value={waterProperties.tds}
                onChange={(e) => handleWaterPropertyChange('tds', e.target.value)}
                onBlur={(e) => handleWaterPropertyBlur('tds', e.target.value)}
              />
            </td>
            <td>mg/L</td>
          </tr>
          <tr>
            <td>Density</td>
            <td className="input-size-3rem">
              <input
                type="text"
                className="number-entry input-size-3rem"
                value={waterProperties.density}
                onChange={(e) => handleWaterPropertyChange('density', e.target.value)}
                onBlur={(e) => handleWaterPropertyBlur('density', e.target.value)}
              />
            </td>
            <td>
              <div className="unit-selector density-selector">
                <span 
                  className="header-group" 
                  onClick={handleDensityUnitClick}
                  style={{ padding: '4px' }}
                >
                  {densityUnit}
                </span>
                {showDensityMenu && (
                  <div className="unit-menu">
                    <div onClick={() => handleDensityUnitSelect('g/ml')}>g/ml</div>
                    <div onClick={() => handleDensityUnitSelect('kg/m³')}>kg/m³</div>
                    <div onClick={() => handleDensityUnitSelect('g/L')}>g/L</div>
                    <div onClick={() => handleDensityUnitSelect('kl/L')}>kl/L</div>
                    <div onClick={() => handleDensityUnitSelect('lb/ft³')}>lb/ft³</div>
                    <div onClick={() => handleDensityUnitSelect('lb/gal')}>lb/gal</div>
                  </div>
                )}
              </div>
            </td>
          </tr>
          <tr>
            <td>Specific Electrical Conductivity</td>
            <td className="input-size-3rem">
              <input
                type="text"
                className="number-entry input-size-3rem"
                value={waterProperties.conductivity}
                onChange={(e) => handleWaterPropertyChange('conductivity', e.target.value)}
                onBlur={(e) => handleWaterPropertyBlur('conductivity', e.target.value)}
              />
            </td>
            <td>
              <div className="unit-selector conductivity-selector">
                <span 
                  className="header-group" 
                  onClick={handleConductivityUnitClick}
                  style={{ padding: '4px' }}
                >
                  {conductivityUnit}
                </span>
                {showConductivityMenu && (
                  <div className="unit-menu">
                    <div onClick={() => handleConductivityUnitSelect('mho/m')}>mho/m</div>
                    <div onClick={() => handleConductivityUnitSelect('mho/cm')}>mho/cm</div>
                    <div onClick={() => handleConductivityUnitSelect('µmho/m')}>µmho/m</div>
                    <div onClick={() => handleConductivityUnitSelect('µmho/cm')}>µmho/cm</div>
                    <div onClick={() => handleConductivityUnitSelect('S/m')}>S/m</div>
                  </div>
                )}
              </div>
            </td>
          </tr>
          <tr>
            <td>Measured TIC</td>
            <td className="input-size-3rem">
              <input
                type="text"
                className="number-entry input-size-3rem"
                value={waterProperties.tic}
                onChange={(e) => handleWaterPropertyChange('tic', e.target.value)}
                onBlur={(e) => handleWaterPropertyBlur('tic', e.target.value)}
              />
            </td>
            <td>
              <div className="unit-selector tic-selector">
                <span 
                  className="header-group" 
                  onClick={handleTicUnitClick}
                  style={{ padding: '4px' }}
                >
                  {ticUnit}
                </span>
                {showTicMenu && (
                  <div className="unit-menu">
                    <div onClick={() => handleTicUnitSelect('mg C/L')}>mg C/L</div>
                    <div onClick={() => handleTicUnitSelect('g C/L')}>g C/L</div>
                    <div onClick={() => handleTicUnitSelect('mol C/L')}>mol C/L</div>
                  </div>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  const handleBulkDataClick = () => {
    setShowBulkDataModal(true);
  };

  const handleBulkDataClose = () => {
    setShowBulkDataModal(false);
  };

  const handleBulkDataConfirm = (data) => {
    setWaterData(prev => ({
      ...prev,
      cations: data.cations,
      anions: data.anions,
      neutrals: data.neutrals
    }));
    setShowBulkDataModal(false);
  };

  const renderReservoirDataInput = () => {
    return (
      <div className="reservoir-data-container">
        <div className="reservoir-tabs">
          <button
            className={`reservoir-tab ${activeReservoirTab === 'water' ? 'active' : ''}`}
            onClick={() => setActiveReservoirTab('water')}
          >
            Water
          </button>
          <button
            className={`reservoir-tab ${activeReservoirTab === 'gas' ? 'active' : ''}`}
            onClick={() => setActiveReservoirTab('gas')}
          >
            Gas
          </button>
          <button
            className={`reservoir-tab ${activeReservoirTab === 'oil' ? 'active' : ''}`}
            onClick={() => setActiveReservoirTab('oil')}
          >
            Oil
          </button>
        </div>
        <div className="reservoir-content">
          {activeReservoirTab === 'water' && (
            <>
              <div className="water-subtabs">
                <button
                  className={`water-subtab ${activeWaterTab === 'chemistry' ? 'active' : ''}`}
                  onClick={() => setActiveWaterTab('chemistry')}
                >
                  Chemistry
                </button>
                <button
                  className={`water-subtab ${activeWaterTab === 'properties' ? 'active' : ''}`}
                  onClick={() => setActiveWaterTab('properties')}
                >
                  Recorded Properties
                </button>
              </div>
              <div className="reservoir-tables-grid">
                {activeWaterTab === 'chemistry' ? (
                  <>
                    {renderTable('Cations', waterData.cations, handleWaterDataChange, 'water', 'cations')}
                    {renderTable('Anions', waterData.anions, handleWaterDataChange, 'water', 'anions')}
                    {renderTable('Neutrals', waterData.neutrals, handleWaterDataChange, 'water', 'neutrals')}
                  </>
                ) : (
                  renderWaterPropertiesTable()
                )}
              </div>
              <div className="bulk-data-link">
                <a href="#" onClick={(e) => { e.preventDefault(); handleBulkDataClick(); }}>
                  paste bulk data
                </a>
              </div>
            </>
          )}
          {activeReservoirTab === 'gas' && (
            <>
              <div className="reservoir-tables-grid">
                {renderTable('Components', gasData.components, handleGasDataChange, 'gas', 'gasComponents')}
                {showGasAssaysTable && renderTable('Assays', gasData.assays, handleGasDataChange, 'gas', 'assays')}
              </div>
            </>
          )}
          {activeReservoirTab === 'oil' && (
            <>
              <div className="reservoir-tables-grid">
                {renderTable('Components', oilData.components, handleOilDataChange, 'oil', 'oilComponents')}
                {renderTable('Pseudo-comp.', oilData.pseudoComponents, handleOilDataChange, 'oil', 'pseudoComponents')}
                {renderTable('Assays', oilData.assays, handleOilDataChange, 'oil', 'assays')}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="App">
      <TitleBar />
      <div className="main-content">
        <img src={logo} alt="OLI Logo" className="background-logo" />
        <div className="opening-panel">
          <h2>What are we working on?</h2>
          <div className="columns">
            <div className="column-1">
              <div className="new-well" onClick={() => setShowOverlay(true)}>
                <img src={gasWellIcon} alt="Gas Well" className="gas-well-icon" />
                <h3>New well</h3>
              </div>
            </div>
            <div className="column-2">
              <h3>Recent</h3>
              <ul className="recent-list">
                <li><a href="#poseiden1"><img src={atomIcon} alt="Atom Icon" />Poseiden 1</a></li>
                <li><a href="#atlantis3"><img src={atomIcon} alt="Atom Icon" />Atlantis 3</a></li>
                <li><a href="#atlantis2"><img src={atomIcon} alt="Atom Icon" />Atlantis 2</a></li>
                <li><a href="#athena5"><img src={atomIcon} alt="Atom Icon" />Athena 5</a></li>
                <li><a href="#atlantis1"><img src={atomIcon} alt="Atom Icon" />Atlantis 1</a></li>
              </ul>
              <div className="browse-container">
                <input
                  type="file"
                  id="file-browser"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  multiple
                />
                <label htmlFor="file-browser" className="browse-link">Browse...</label>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showOverlay && (
        <div className="overlay">
          <div className="overlay-content">
            <button className="close-button" onClick={handleCancel}>×</button>
            {showSampleData && (
              <a href="#" className="back-link" onClick={(e) => { e.preventDefault(); handleBack(); }}>
                &lt; back
              </a>
            )}
            <h2>{showSampleData ? wellName : (showConditions ? wellName : 'New Well')}</h2>
            {!showConditions && !showSampleData ? (
              <>
                <div className="input-group">
                  <label htmlFor="well-name">Well name:</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      id="well-name"
                      required
                      value={wellName}
                      onChange={(e) => {
                        setWellName(e.target.value);
                        if (showError) {
                          setShowError(false);
                        }
                      }}
                    />
                    <div className={`error-message ${showError ? 'show' : ''}`}>You must enter a well name</div>
                  </div>
                </div>
                <h3>Well components</h3>
                <p>We will build your well out of the following components.</p>
                <div className="checkbox-list">
                  <div className="checkbox-item">
                    <input type="checkbox" id="separator" checked disabled />
                    <label htmlFor="separator">Separator</label>
                  </div>
                  <div className="checkbox-item">
                    <input type="checkbox" id="choke-valve" checked disabled />
                    <label htmlFor="choke-valve">Choke valve</label>
                  </div>
                  <div className="checkbox-item">
                    <input type="checkbox" id="well-head" checked disabled />
                    <label htmlFor="well-head">Well head</label>
                  </div>
                  <div className="checkbox-item">
                    <input type="checkbox" id="downhole" checked disabled />
                    <label htmlFor="downhole">Downhole</label>
                  </div>
                  <div className="checkbox-item">
                    <input type="checkbox" id="reservoir" checked disabled />
                    <label htmlFor="reservoir">Reservoir</label>
                  </div>
                </div>
              </>
            ) : showConditions ? (
              <>
                <h3>Well conditions</h3>
                <p>Please enter all the conditions you have measurements for within your well:</p>
                <table className="conditions-table">
                  <tbody>
                    <tr>
                      <td></td>
                      <td>
                        <div className="unit-selector temp-selector">
                          <span 
                            className="header-group" 
                            onClick={handleTempClick}
                          >
                            <i className="fa fa-thermometer-half header-icon"></i> {tempUnit}
                          </span>
                          {showTempMenu && (
                            <div className="unit-menu">
                              <div onClick={() => handleUnitSelect('°C', 'temp')}>°C</div>
                              <div onClick={() => handleUnitSelect('°F', 'temp')}>°F</div>
                              <div onClick={() => handleUnitSelect('K', 'temp')}>K</div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="unit-selector pressure-selector">
                          <span 
                            className="header-group" 
                            onClick={handlePressureClick}
                          >
                            <img src={pressureIcon} alt="Pressure" className="header-icon" /> {pressureUnit}
                          </span>
                          {showPressureMenu && (
                            <div className="unit-menu">
                              <div onClick={() => handleUnitSelect('bar', 'pressure')}>bar</div>
                              <div onClick={() => handleUnitSelect('atm', 'pressure')}>atm</div>
                              <div onClick={() => handleUnitSelect('psi', 'pressure')}>psi</div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>Separator</td>
                      <td>
                        <input 
                          type="text" 
                          value={conditions.separator.temp}
                          onChange={(e) => handleInputChange('separator', 'temp', e.target.value)}
                          onBlur={(e) => handleInputBlur('separator', 'temp', e.target.value)}
                        />
                      </td>
                      <td>
                        <input 
                          type="text" 
                          value={conditions.separator.pressure}
                          onChange={(e) => handleInputChange('separator', 'pressure', e.target.value)}
                          onBlur={(e) => handleInputBlur('separator', 'pressure', e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Choke valve</td>
                      <td>
                        <input 
                          type="text" 
                          value={conditions.chokeValve.temp}
                          onChange={(e) => handleInputChange('chokeValve', 'temp', e.target.value)}
                          onBlur={(e) => handleInputBlur('chokeValve', 'temp', e.target.value)}
                        />
                      </td>
                      <td>
                        <input 
                          type="text" 
                          value={conditions.chokeValve.pressure}
                          onChange={(e) => handleInputChange('chokeValve', 'pressure', e.target.value)}
                          onBlur={(e) => handleInputBlur('chokeValve', 'pressure', e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Well head</td>
                      <td>
                        <input 
                          type="text" 
                          value={conditions.wellHead.temp}
                          onChange={(e) => handleInputChange('wellHead', 'temp', e.target.value)}
                          onBlur={(e) => handleInputBlur('wellHead', 'temp', e.target.value)}
                        />
                      </td>
                      <td>
                        <input 
                          type="text" 
                          value={conditions.wellHead.pressure}
                          onChange={(e) => handleInputChange('wellHead', 'pressure', e.target.value)}
                          onBlur={(e) => handleInputBlur('wellHead', 'pressure', e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Downhole</td>
                      <td>
                        <input 
                          type="text" 
                          value={conditions.downhole.temp}
                          onChange={(e) => handleInputChange('downhole', 'temp', e.target.value)}
                          onBlur={(e) => handleInputBlur('downhole', 'temp', e.target.value)}
                        />
                      </td>
                      <td>
                        <input 
                          type="text" 
                          value={conditions.downhole.pressure}
                          onChange={(e) => handleInputChange('downhole', 'pressure', e.target.value)}
                          onBlur={(e) => handleInputBlur('downhole', 'pressure', e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>Reservoir</td>
                      <td>
                        <input 
                          type="text" 
                          value={conditions.reservoir.temp}
                          onChange={(e) => handleInputChange('reservoir', 'temp', e.target.value)}
                          onBlur={(e) => handleInputBlur('reservoir', 'temp', e.target.value)}
                        />
                      </td>
                      <td>
                        <input 
                          type="text" 
                          value={conditions.reservoir.pressure}
                          onChange={(e) => handleInputChange('reservoir', 'pressure', e.target.value)}
                          onBlur={(e) => handleInputBlur('reservoir', 'pressure', e.target.value)}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            ) : (
              <>
                <h3 className="sample-data-title">Sample Data</h3>
                {renderReservoirDataInput()}
              </>
            )}
            <div className="button-group">
              <button className="cancel-button" onClick={handleCancel}>Cancel</button>
              <button 
                className="next-button" 
                onClick={handleNext}
                disabled={showConditions && !hasValidConditions()}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
      {showPreviewModal && renderPreviewModal()}
      {showBulkDataModal && (
        <BulkDataModal
          onClose={handleBulkDataClose}
          onConfirm={handleBulkDataConfirm}
          dataType="water"
        />
      )}
    </div>
  );
}

export default App;
